import React from 'react';
import { SchematicShell, Rect, Line } from './SchematicPrimitives';

const INCH = 0.0254;

export function BarStoolSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const backrestH = 7 * INCH;
  const totalH = height + backrestH;

  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={totalH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
        drawFrame={false}
      >
        <g
          transform={
            view === 'right' ? `scale(-1, 1) translate(${-depth}, 0)` : ''
          }
        >
          <SideProfile
            width={width}
            height={height}
            depth={depth}
            backrestH={backrestH}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        </g>
      </SchematicShell>
    );
  }

  return (
    <SchematicShell
      width={width}
      height={totalH}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      <FrontProfile
        width={width}
        height={height}
        depth={depth}
        backrestH={backrestH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </SchematicShell>
  );
}

// Shared stool geometry, derived once for both profiles.
// Matches `barStoolRenderer` in furnitureRenderers.js: 2" seat pad, 0.4"
// frame tube, legs splayed at PI/4.5, footrest ring 8" off the floor
// interpolated between leg top/bottom spread.
function computeStoolGeometry(width, height, depth, backrestH) {
  const seatR = Math.min(width, depth) / 2;
  const padH = 2 * INCH;
  const frameTube = 0.4 * INCH;
  const panelH = 6 * INCH;
  const postAngle = Math.PI / 4.5;
  const topInset = seatR * 0.5;
  const botSpread = Math.max(width, depth) / 2 + 2 * INCH;
  const diag = Math.SQRT1_2;
  const legR = 0.3 * INCH;

  const seatTopY = backrestH;
  const seatBotY = seatTopY + padH + frameTube;
  const floorY = height + backrestH;
  const footrestY = floorY - 8 * INCH;

  const legTravel = height - padH - frameTube;
  const ft = (8 * INCH) / legTravel;
  const ringR = botSpread * (1 - ft) + topInset * ft;

  return {
    seatR,
    padH,
    frameTube,
    panelH,
    postAngle,
    postW: legR * 2,
    legTopOff: topInset * diag,
    legBotOff: botSpread * diag,
    seatTopY,
    seatBotY,
    floorY,
    footrestY,
    ringR,
  };
}

function FrontProfile({
  width,
  height,
  depth,
  backrestH,
  strokeColor,
  strokeWidth,
}) {
  const {
    seatR,
    padH,
    frameTube,
    panelH,
    postAngle,
    postW,
    legTopOff: legTopX,
    legBotOff: legBotX,
    seatTopY,
    seatBotY,
    floorY,
    footrestY,
    ringR,
  } = computeStoolGeometry(width, height, depth, backrestH);

  const overhang = Math.PI / 10;
  const cx = width / 2;
  const postXoff = seatR * Math.sin(postAngle);
  const panelHalfW = seatR * Math.sin(postAngle + overhang);

  return (
    <g>
      {/* Backrest panel */}
      <Rect
        x={cx - panelHalfW}
        y={0}
        width={panelHalfW * 2}
        height={panelH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Left backrest post */}
      <Rect
        x={cx - postXoff - postW / 2}
        y={0}
        width={postW}
        height={backrestH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Right backrest post */}
      <Rect
        x={cx + postXoff - postW / 2}
        y={0}
        width={postW}
        height={backrestH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Seat pad */}
      <Rect
        x={cx - seatR}
        y={seatTopY}
        width={seatR * 2}
        height={padH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Frame ring */}
      <Line
        x1={cx - seatR * 0.9}
        y1={seatTopY + padH + frameTube / 2}
        x2={cx + seatR * 0.9}
        y2={seatTopY + padH + frameTube / 2}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Left legs */}
      <Line
        x1={cx - legTopX}
        y1={seatBotY}
        x2={cx - legBotX}
        y2={floorY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Right legs */}
      <Line
        x1={cx + legTopX}
        y1={seatBotY}
        x2={cx + legBotX}
        y2={floorY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Footrest ring */}
      <Line
        x1={cx - ringR}
        y1={footrestY}
        x2={cx + ringR}
        y2={footrestY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

function SideProfile({
  width,
  height,
  depth,
  backrestH,
  strokeColor,
  strokeWidth,
}) {
  const {
    seatR,
    padH,
    frameTube,
    panelH,
    postAngle,
    postW,
    legTopOff: legTopZ,
    legBotOff: legBotZ,
    seatTopY,
    seatBotY,
    floorY,
    footrestY,
    ringR,
  } = computeStoolGeometry(width, height, depth, backrestH);

  const panelThick = 1 * INCH;
  const cx = depth / 2;
  const postZoff = seatR * Math.cos(postAngle);

  return (
    <g>
      {/* Backrest panel (edge-on) */}
      <Rect
        x={cx - postZoff - panelThick / 2}
        y={0}
        width={panelThick}
        height={panelH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Backrest post */}
      <Rect
        x={cx - postZoff - postW / 2}
        y={0}
        width={postW}
        height={backrestH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Seat pad */}
      <Rect
        x={cx - seatR}
        y={seatTopY}
        width={seatR * 2}
        height={padH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Frame ring */}
      <Line
        x1={cx - seatR * 0.9}
        y1={seatTopY + padH + frameTube / 2}
        x2={cx + seatR * 0.9}
        y2={seatTopY + padH + frameTube / 2}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Back leg */}
      <Line
        x1={cx - legTopZ}
        y1={seatBotY}
        x2={cx - legBotZ}
        y2={floorY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Front leg */}
      <Line
        x1={cx + legTopZ}
        y1={seatBotY}
        x2={cx + legBotZ}
        y2={floorY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Footrest ring */}
      <Line
        x1={cx - ringR}
        y1={footrestY}
        x2={cx + ringR}
        y2={footrestY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

BarStoolSchematic.supportsSideViews = true;
// A stool seen from behind is its own silhouette.
BarStoolSchematic.supportsBackView = true;
